<template>
  <div v-if="open" class="confirm-modal-backdrop" @click.self="onCancel">
    <div class="confirm-modal" role="dialog" aria-modal="true">
      <div class="confirm-modal-header">
        <h3>{{ title }}</h3>
        <button type="button" class="confirm-modal-close" @click="onCancel" aria-label="Mbyll">&times;</button>
      </div>
      <div class="confirm-modal-body">
        <p class="confirm-modal-message">{{ message }}</p>
        <ul v-if="details && details.length" class="confirm-modal-details">
          <li v-for="(d, i) in details" :key="i">{{ d }}</li>
        </ul>
        <p v-if="warning" class="confirm-modal-warning">{{ warning }}</p>
        <div v-if="allowSchedule" class="confirm-modal-timing">
          <div class="confirm-timing-switch">
            <button
              type="button"
              class="confirm-timing-btn"
              :class="{ active: timing === 'now' }"
              :disabled="loading"
              @click="timing = 'now'"
            >
              Apliko tani
            </button>
            <button
              type="button"
              class="confirm-timing-btn"
              :class="{ active: timing === 'scheduled' }"
              :disabled="loading"
              @click="timing = 'scheduled'"
            >
              Planifiko
            </button>
          </div>
          <p v-if="scheduleOverCap" class="confirm-modal-schedule-note">
            Ke {{ scheduleItemCount }} produkte. Do të ndahet automatikisht në {{ scheduleSplitCount }} batch-e (max {{ scheduleItemCap }} secili).
          </p>
          <label v-if="timing === 'scheduled'">
            Data dhe ora
            <input
              v-model="applyAtLocal"
              class="input-clean"
              type="datetime-local"
              :min="minApplyAtLocal"
              :disabled="loading"
            />
          </label>
          <p v-if="timing === 'scheduled'" class="confirm-modal-schedule-note">
            Çmimet ruhen siç i parashikove dhe aplikohen në orarin e zgjedhur. Mund t’i anulosh nga auditi para asaj ore.
          </p>
          <p v-if="timing === 'scheduled' && applyAtError" class="confirm-modal-schedule-error">
            {{ applyAtError }}
          </p>
        </div>
        <div v-if="confirmWord" class="confirm-modal-type">
          <label>
            Shkruaj <strong>{{ confirmWord }}</strong> për të vazhduar:
            <input
              ref="confirmInput"
              v-model="typed"
              class="input-clean"
              type="text"
              autocomplete="off"
              @keyup.enter="onConfirm"
            />
          </label>
        </div>
      </div>
      <div class="confirm-modal-actions">
        <button type="button" class="button is-light" :disabled="loading" @click="onCancel">
          {{ cancelLabel }}
        </button>
        <button
          type="button"
          class="button"
          :class="[danger ? 'is-danger' : 'is-primary', { 'is-loading': loading }]"
          :disabled="!canConfirm || loading"
          @click="onConfirm"
        >
          {{ displayConfirmLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
function pad2(n) {
  return String(n).padStart(2, "0");
}

function toLocalDatetimeValue(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function defaultApplyAtLocal() {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(0);
  d.setHours(d.getHours() + 1);
  return toLocalDatetimeValue(d);
}

export default {
  name: "ConfirmActionModal",
  props: {
    open: { type: Boolean, default: false },
    title: { type: String, default: "Konfirmo" },
    message: { type: String, default: "" },
    warning: { type: String, default: "" },
    details: { type: Array, default: () => [] },
    confirmWord: { type: String, default: "" },
    confirmLabel: { type: String, default: "Vazhdo" },
    cancelLabel: { type: String, default: "Anulo" },
    danger: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    allowSchedule: { type: Boolean, default: false },
    scheduleItemCount: { type: Number, default: 0 },
    scheduleItemCap: { type: Number, default: 2000 },
  },
  data() {
    return {
      typed: "",
      timing: "now",
      applyAtLocal: "",
    };
  },
  computed: {
    minApplyAtLocal() {
      return toLocalDatetimeValue(new Date());
    },
    scheduleOverCap() {
      return Number(this.scheduleItemCount || 0) > Number(this.scheduleItemCap || 0);
    },
    scheduleSplitCount() {
      const cap = Number(this.scheduleItemCap || 1);
      const count = Number(this.scheduleItemCount || 0);
      if (count <= 0) return 1;
      return Math.max(1, Math.ceil(count / cap));
    },
    applyAtError() {
      if (!this.allowSchedule || this.timing !== "scheduled") return "";
      if (!this.applyAtLocal) return "Zgjidh datën dhe orën.";
      const d = new Date(this.applyAtLocal);
      if (Number.isNaN(d.getTime())) return "Ora nuk është e vlefshme.";
      if (d.getTime() <= Date.now()) return "Orari duhet të jetë në të ardhmen.";
      return "";
    },
    displayConfirmLabel() {
      if (this.allowSchedule && this.timing === "scheduled") return "Planifiko";
      return this.confirmLabel;
    },
    canConfirm() {
      if (this.allowSchedule && this.timing === "scheduled" && this.applyAtError) return false;
      if (!this.confirmWord) return true;
      return String(this.typed || "").trim().toUpperCase() === String(this.confirmWord).trim().toUpperCase();
    },
  },
  watch: {
    open(val) {
      if (val) {
        this.typed = "";
        this.timing = "now";
        this.applyAtLocal = defaultApplyAtLocal();
        this.$nextTick(() => {
          this.$refs.confirmInput?.focus?.();
        });
      }
    },
  },
  methods: {
    onCancel() {
      if (this.loading) return;
      this.$emit("cancel");
    },
    onConfirm() {
      if (!this.canConfirm || this.loading) return;
      let applyAt = null;
      if (this.allowSchedule && this.timing === "scheduled") {
        const d = new Date(this.applyAtLocal);
        applyAt = d.toISOString();
      }
      this.$emit("confirm", { timing: this.timing, applyAt });
    },
  },
};
</script>

<style scoped>
.confirm-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 80;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}
.confirm-modal {
  width: min(520px, 100%);
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
  overflow: hidden;
}
.confirm-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px 8px;
}
.confirm-modal-header h3 {
  margin: 0;
  font-size: 1.1rem;
}
.confirm-modal-close {
  border: 0;
  background: transparent;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  color: #64748b;
}
.confirm-modal-body {
  padding: 8px 18px 12px;
}
.confirm-modal-message {
  margin: 0 0 10px;
  color: #334155;
  line-height: 1.45;
}
.confirm-modal-details {
  margin: 0 0 12px;
  padding-left: 18px;
  color: #475569;
}
.confirm-modal-warning {
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fff7ed;
  color: #9a3412;
  font-size: 0.92rem;
}
.confirm-modal-timing {
  margin: 0 0 12px;
}
.confirm-timing-switch {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
}
.confirm-timing-btn {
  flex: 1;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #475569;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 0.9rem;
  cursor: pointer;
}
.confirm-timing-btn.active {
  background: #eef2ff;
  border-color: #c7d2fe;
  color: #3730a3;
  font-weight: 600;
}
.confirm-modal-timing label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.9rem;
  color: #334155;
}
.confirm-modal-timing input {
  width: 100%;
}
.confirm-modal-schedule-note {
  margin: 8px 0 0;
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.4;
}
.confirm-modal-schedule-error {
  margin: 6px 0 0;
  font-size: 0.85rem;
  color: #b91c1c;
}
.confirm-modal-type label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.9rem;
  color: #334155;
}
.confirm-modal-type input {
  width: 100%;
}
.confirm-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 18px 16px;
  border-top: 1px solid #e2e8f0;
}
</style>
